import { and, not, or, setup } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

import type { TContext, TEvent } from 'machines/types/machine-config';

export const abandonedCartMachineConfig = {
  id: 'abandonedCartMachine',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: {
          target: 'quote'
        },
        QUOTE_EXPIRED: {
          target: 'quoteExpired'
        }
      },
      meta: {
        path: routePath.abandonedCart
      }
    },

    quoteExpired: {
      on: {
        NEXT: [
          {
            target: 'energyNeeds',
            guard: 'hasAnyMeterValidationRequired'
          },
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'energyNeeds'
          }
        ]
      },
      meta: {
        path: routePath.quoteExpiredAbandonedCart
      }
    },

    meterpointValidation: {
      on: {
        NEXT: [
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'energyNeeds'
          }
        ],
        GUIDE_CLICK: {
          target: 'meterPointGuide'
        }
      },
      meta: {
        path: routePath.meterpointValidation
      }
    },

    meterPointGuide: {
      meta: {
        path: routePath.meterpointGuide
      }
    },

    selectOptions: {
      on: {
        ELIGIBLE_FUEL: {
          target: 'energyNeeds'
        },
        EDIT_ADDRESS: {
          target: 'home'
        },
        HOME_MOVE: [
          {
            target: 'externalContactUs',
            description: `or([
                'isMixedNepSap',
                and([not('allowJourneyMoveIn'), 'isBGECustomer'])
              ])`,

            guard: or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])
          },
          {
            target: 'externalMoveIn',
            guard: 'isBGECustomer'
          },
          {
            target: 'externalBgClassicHomeMove'
          }
        ],
        MIXED_METERS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.selectOptions
      }
    },

    energyNeeds: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'meterValidationRequired'
          },
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'evTariffInformation',
            guard: 'showEvTariffInformation'
          },
          {
            target: 'quote'
          }
        ]
      },
      meta: {
        path: routePath.energyNeeds
      }
    },

    evTariffInformation: {
      on: {
        NEXT: [
          {
            target: 'quote'
          }
        ]
      },
      meta: {
        path: routePath.evTariffInformation
      }
    },

    quote: {
      on: {
        NEXT: [
          {
            target: 'peakSave',
            guard: 'hasElectricityTOUMeter'
          },
          {
            target: 'personalDetails'
          }
        ],
        CONSUMPTION: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    peakSave: {
      on: {
        NEXT: {
          target: 'personalDetails'
        }
      },
      meta: {
        path: routePath.peakSave
      }
    },

    personalDetails: {
      on: {
        NEXT: [
          {
            target: 'priorityServicesRegister',
            guard: 'hasPSR'
          },
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'orderSummary'
          }
        ]
      },
      meta: {
        path: routePath.personalDetails
      }
    },

    priorityServicesRegister: {
      on: {
        NEXT: [
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'orderSummary'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },

      meta: {
        path: routePath.directDebit
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        },
        CHANGE_PERSONAL_DETAILS: {
          target: 'personalDetails'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_PAYMENT_DETAILS: {
          target: 'directDebit'
        },
        CHANGE_PSR_DETAILS: {
          target: 'priorityServicesRegister'
        },
        CHANGE_ENERGY_NEEDS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },

    orderComplete: {
      on: {
        SALE_HANDOFF: {
          target: 'externalSaleHandoff'
        }
      },
      meta: {
        path: routePath.orderComplete
      }
    },

    externalBgSalesApp: {
      meta: {
        path: actionUrl.bgSalesApp
      }
    },

    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },

    externalMoveIn: {
      meta: { path: actionUrl.moveIn }
    },

    externalSaleHandoff: {
      meta: { path: actionUrl.saleHandoff }
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...(events as any)
};

const { VITE_XSTATE_DEBUG } = import.meta.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  setup({
    types: {} as {
      context: TContext;
      events: TEvent;
    },
    guards
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QEMBGyB2ED2HIGFkAnAFwFlkBjACwEs8A6a7AWzAGIA5AUQA0AVANoAGALqJQAB2yxaJWrgkgAHogCMAVjUMAbDrUAWAEwBOAOwmtGgxp0AaEAE9EJowA4GGgMw6vts2omXpY6AL6hDmiYOHgQhKQUNPRgTKwcAIoAqgDy-NwA+nwACgCSAErcACIi4kgg0rLyinWqCJraeoamFlY29k6IZgYeOmYaZl5uWqM6Bl7hkehYuATE5FR0jACOAK7YJGDcypK0RJBcfEJiSg1yChhKreMeJgbCXmZmbm5GRsMOzgQYw8ph+XiMEzUXx0RgWICiy1i8XWSW2ewORxOZwgFwEgjUtSkMjuzVATy+DFe70+31+-wGbR0JgYELUszeanBekscIRMVWCQ2yQYu32h2Op3OPDxRkJ9WJTQeLUQz0pbw+Xx+fzcAMGwg8BjM7jZQ2EwgMel5S35cTWiU2KVgYAANmBKCRspJFbBcVc5bdFY91AFhAxhBMTMI1EYNG4DAYTLqEBqWcIYfq3LMxrGrdEVrbBajHS63R6vfcfdKhASbgr7kG2pyzJThBpvGpvl4+mYk-HmxY-GbM99M7nEQKUQ6GE7Xe7Pd7fYJZbXGvXlW0Q2GI1GY3GE0mvB2GAEYeCzRbRmMxzbkfbhTPS-OK4uvP666SVMG1KHw8Ed7H40TBkjB0UNfhhNtDBsCwzGvfNbyFRgHznctcErS5BAMN9Vw-VooW-Lc-2jAD92AqNjz8TNbAtYi3HmCJ4WteC7UQ4tZzLBcyBKXgqnyMhuDyMoAGUahXEklTJdRTA0Y8-j0cYTA7YIewZLxWwYOYvB8IZRhjYQQLgpEWKLBgwDwIgoEcTgwEgdC8WuOoAzXSTGw+VM3AmYZvAApNPgMXQ03MN5rEPIJDInO9GDMsALKsmyIDs6tsPEhs1CbdzPKmSiDAPNsWQ0ExQI8vwjWjMIGL5ZjCynaLYus2zF2XRz3wkz9XObIx9Uy7y4yTaxm3jTT3A0IwfG8cKC0nYVass+qEpfZLA3XNK3M64qvOy3tzF0DtFJ0TM0xjXwJoQkywAAN34YhaAAMxukoMBu7AiBYZBFUXByiRw1rWl+NxmT+d4oVmUEkz0UNQMUwHhBMRTFJO4yp1FA4PsW5y2pjI0GDUswowtAJSMBblPD+aMo28ECjQR6rhWRjgq3xNHcMQTGjGx8M8dGQwgMBaxmTSwxo00CMhmpqa0TFdh8GyTghMyMgin4EoZdE5rvobP6AfVYHjBMHUGTcbRwUPcYdA0fTbCjMXIpSSQwGQABrITkHO+mMM++V1fXUwgjDOY5gmONI3GMGmRJgwyc0LkqYqpijJpxg7aIWBcGQZ1KjAEhkFoZ1EtVr6UvXLSPLDcME1K02ISTLof0CVs+kFgxrdYhgk5TjA04zrOc7zms1cLlzi+bM0hnMIxhZhFTAVcEEjVjYJzHNCPm5MtvU-TzPs9zxqmZ+lnYa8P2uw+SYE1bKeXGCTwfD8LmghCFep0kIgFBfkhHCEmLztoSg4DKMAoC0FgAcIgqMxJLUHsHMM49-qFUjMYC01cLTMj3IaVs7ZvKP2FM-V+cgP5fx-n-ABQCQGLj7gXCBbUuyxlTJbGGUZQIXzaMghgqDcZthNn4LBjAICSndBnVAcgwH90oa0YKzYPKKQKq4JkEd+iAmjLDAKBUtKuACF8eiiw8zx3FikZ6EAYpCR2CwV6RBHDCIoejckLx1Q0i1PSBRrZmSvEjFmTQIFrDcL0UQAxRAjEmOIOY-AAAJAAgpwAA4gUIo3BhIy1CQAGXyJUASoSSgJJEh7JyzMECqipBqWk2pq5Qn5kEXwUwmSjBsF4hg+jDHGNMUEsJkSCihMqJUCoQlMm71SloXQ+hdY9E0H0aup4NKGjrpGYisJY7aIii3OpfiGmBKls0qJ+QiihIAJr8U4PwZJqT0ndPAVYlUFJ8l2LpPrBRswfw-AKjYN4ZhjqzPHJNG2tSfH1ICWY1Z4T1lFCEmUA5-A0kZPzp7AebU8m2M1Fc6uBUwLjChFMM+o0m6vJvIjYUiz-GNL+S0woPAygRK2fkHgVRjkiNObk85sLCkOMQG4fUDBow+GGEaPwMYTA1LYCA6Q9ASAADU060AgG9e4FjIWiK-D+bctFAKhwkeMGwXZMygVbLyzOMUBUYGFaK8V70GbkOlTS-CcqiK7kVQyL42h9KCzKUMUa9EGIYGwAY+AdRKo6JticnJABaeRiBA2lzNGG8NZpYYaBqcwNgfq94IGMKMzQLJjA-G+PA8e5UtFvNOkjdE4osSQHjQ2Hw2h4x6BpObN4XlfLDFYf7WM+puVW0xVVXR04SwoW9CW5a5sdDHi6KNQwUxXhBuTGbVhIEgjeDnm2apbafUtxmnFWyvaXIz1Ya40CZVgg-F8jQi0BURrdH0OPGpF0rovzug9J6L0JUfmyQmzq8ZWEDLbO4Q0yDQ6hk+PtOY4wVUJhqXTddGNDQDsCFpDm1gISzFDofODcYSpAZ5Yu+Zq97ZOxdmAMDv1hzY1GvpfanUzbvFDsyJkSlzCTM0YxOZ7yW5rw7hvbuuc8OIHBIEXQWhzRdi+HMSwSYgj+SmG2Z5+pYZDGjehxjq8X7PTwZ-Ig39f6wH-oA4BMUOMTqGKwzkpGo2CyQbjMMQspm7QHDU3hZx+FgEESQHTEdTO6W7LDYw47DCjH6aFP44JF7Zvo7m7FjBcXLLMTpvwHR9DeGZVFipozRo7Qpoeeu-samLPwKwSQroDg6dcIfT4zzZ36neFoauaZQxpQCHrCpRoYRav5dgQVIrnRiofa1J9qVhiFeNnoAZFTx1+WPGMb8htLBfAvcoEBLGABCUBnaulgKEyQkgdOdXHroMYsiP36FcAeI8nZLDC3NDOjFOasUJxSGAabMUWNZb1VQEgmRPWWJyRtjo23Xi7cCEYA8nxsYRk-WpWMbIpszbTvN-AzpkCwFkJQYJaQyDYFdut+1W3bDfZGntv7ZFtCRgXl8c2h5RaybzdNW7RAWPI9dg9NHm3RiY5sNj37SYQKhmCMpCZ5p0HhHCEAA */
    ...abandonedCartMachineConfig
  });
